import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { PROJECT_MANAGEMENT_URL } from '@insite-store360/shared';
import { Observable, catchError } from 'rxjs';
import { ErrorHandlerService } from './error-handler.service';

export interface UserApprovalMappingItem {
  id: number;
  userId: number;
  userName: string;
  approverId: number;
  approverName: string;
  orderId: number;
  delegationUserId?: number;
  delegationUserName?: string;
  countryId?: number;
  countryName?: string;
  createdDateTime?: Date;
  updatedDateTime?: Date;
}

@Injectable({
  providedIn: 'root',
})
export class UserApprovalMappingService {
  private readonly URL_PATH = `${PROJECT_MANAGEMENT_URL}/user-approval-mapping`;

  constructor(
    private http: HttpClient,
    private errorHandler: ErrorHandlerService
  ) { }

  /**
   * Get all user approval mappings with pagination
   */
  public getAll(
    pageIndex?: number,
    pageSize?: number,
    searchValue?: string,
    userId?: number,
    countryId?: number
  ): Observable<{ data: UserApprovalMappingItem[]; paginatorValue: { totalData: number; totalCount: number } }> {
    let params = new HttpParams();
    if (pageIndex !== undefined && pageIndex !== null) {
      params = params.set('pageIndex', pageIndex.toString());
    }
    if (pageSize !== undefined && pageSize !== null) {
      params = params.set('pageSize', pageSize.toString());
    }
    if (searchValue && searchValue.trim() !== '') {
      params = params.set('searchValue', searchValue.trim());
    }
    if (userId !== undefined && userId !== null) {
      params = params.set('userId', userId.toString());
    }
    if (countryId !== undefined && countryId !== null) {
      params = params.set('countryId', countryId.toString());
    }
    return this.http
      .get<{ data: UserApprovalMappingItem[]; paginatorValue: { totalData: number; totalCount: number } }>(`${this.URL_PATH}/getAll`, { params })
      .pipe(catchError(this.errorHandler.handleError));
  }

  /**
   * Delete all approval mappings for a user and country
   */
  public deleteByUserAndCountry(userId: number, countryId: number): Observable<{ message: string }> {
    let params = new HttpParams();
    params = params.set('userId', userId.toString());
    params = params.set('countryId', countryId.toString());
    
    return this.http
      .delete<{ message: string }>(`${this.URL_PATH}/deleteByUserAndCountry`, { params })
      .pipe(catchError(this.errorHandler.handleError));
  }

  /**
   * Create multiple approval mappings for a user
   */
  public create(
    userId: number,
    countryId: number,
    approvers: Array<{ approverId: number; orderId: number; delegationUserId?: number }>
  ): Observable<{ message: string; result: UserApprovalMappingItem[] }> {
    return this.http
      .post<{ message: string; result: UserApprovalMappingItem[] }>(`${this.URL_PATH}/create`, {
        userId,
        countryId,
        approvers
      })
      .pipe(catchError(this.errorHandler.handleError));
  }

  /**
   * Update approval mappings for a user (handles create/update/delete in one call)
   */
  public update(
    userId: number,
    countryId: number,
    approvers: Array<{ approverId: number; orderId: number; mappingId?: number; delegationUserId?: number }>
  ): Observable<{ message: string; result: UserApprovalMappingItem[] }> {
    return this.http
      .put<{ message: string; result: UserApprovalMappingItem[] }>(`${this.URL_PATH}/update`, {
        userId,
        countryId,
        approvers
      })
      .pipe(catchError(this.errorHandler.handleError));
  }

  /**
   * Get project management users for dropdown
   */
  public getProjectManagementUsers(searchFilter?: string, countryId?: number, roleId?: number): Observable<{ result: { userId: number; fullName: string; email: string }[] }> {
    let queryParams = new HttpParams();
    if (searchFilter && searchFilter.trim() !== '') {
      queryParams = queryParams.set('searchValue', searchFilter.trim());
    }
    if (countryId !== undefined && countryId !== null) {
      queryParams = queryParams.set('countryId', countryId.toString());
    }
    if (roleId !== undefined && roleId !== null) {
      queryParams = queryParams.set('roleId', roleId.toString());
    }
    return this.http
      .get<{ result: { userId: number; fullName: string; email: string }[] }>(`${this.URL_PATH}/getProjectManagementUsers`, { params: queryParams })
      .pipe(catchError(this.errorHandler.handleError));
  }

  /**
   * Get approval users for a user based on user_approval_mapping table
   * Defaults to current logged-in user if userId is not provided
   */
  public getApprovalUsersForUser(countryId?: number, searchValue?: string, userId?: number): Observable<{ result: { userId: number; fullName: string; email: string; delegationUserId?: number; delegationUserName?: string }[] }> {
    let queryParams = new HttpParams();
    if (countryId !== undefined && countryId !== null) {
      queryParams = queryParams.set('countryId', countryId.toString());
    }
    if (searchValue && searchValue.trim() !== '') {
      queryParams = queryParams.set('searchValue', searchValue.trim());
    }
    if (userId !== undefined && userId !== null) {
      queryParams = queryParams.set('userId', userId.toString());
    }
    return this.http
      .get<{ result: { userId: number; fullName: string; email: string; delegationUserId?: number; delegationUserName?: string }[] }>(`${this.URL_PATH}/getApprovalUsersForUser`, { params: queryParams })
      .pipe(catchError(this.errorHandler.handleError));
  }
}

